'use client';

import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import { AISettings, ProviderInfo, ConnectionTestResult, CostEstimate } from '../types/ai-settings';
import { LocalStorageAISettingsStore } from '../services/settings/ai-settings-store';
import { SecureAPIKeyManager } from '../services/security/api-key-manager';
import { AIService } from '../services/ai/types';

interface AISettingsContextType {
  settings: AISettings;
  providers: ProviderInfo[];
  isLoading: boolean;
  hasUnsavedChanges: boolean;
  
  // Settings management
  updateSettings: (updates: Partial<AISettings>) => void;
  saveSettings: () => Promise<void>;
  resetSettings: () => Promise<void>;
  exportSettings: () => Promise<string>;
  importSettings: (data: string) => Promise<void>;
  
  // API key management
  updateApiKey: (provider: string, key: string) => Promise<void>;
  removeApiKey: (provider: string) => Promise<void>;
  validateApiKey: (provider: string, key: string) => Promise<boolean>;
  
  // Provider management
  testConnection: (provider: string) => Promise<ConnectionTestResult>;
  switchProvider: (providerId: string) => void;
  switchModel: (modelId: string) => void;
  
  // Cost estimation
  estimateCost: (inputTokens: number, outputTokens: number) => CostEstimate | null;
}

const AISettingsContext = createContext<AISettingsContextType | undefined>(undefined);

// Provider definitions
const PROVIDER_INFO: ProviderInfo[] = [
  {
    id: 'openai',
    name: 'OpenAI',
    icon: '🤖',
    description: 'GPT-4, GPT-3.5, and DALL-E models',
    website: 'https://openai.com',
    requiresApiKey: true,
    customizable: false,
    status: 'disconnected',
    models: [
      {
        id: 'gpt-4-turbo-preview',
        name: 'GPT-4 Turbo',
        description: 'Most capable model with 128k context',
        contextWindow: 128000,
        maxTokens: 4096,
        supports: {
          chat: true,
          completion: true,
          functionCalling: true,
          vision: true,
          streaming: true,
          jsonMode: true,
        },
        pricing: {
          currency: 'USD',
          input: 0.01,
          output: 0.03,
        },
      },
      {
        id: 'gpt-4',
        name: 'GPT-4',
        description: 'Advanced reasoning capabilities',
        contextWindow: 8192,
        maxTokens: 4096,
        supports: {
          chat: true,
          completion: true,
          functionCalling: true,
          vision: false,
          streaming: true,
          jsonMode: true,
        },
        pricing: {
          currency: 'USD',
          input: 0.03,
          output: 0.06,
        },
      },
      {
        id: 'gpt-3.5-turbo',
        name: 'GPT-3.5 Turbo',
        description: 'Fast and cost-effective',
        contextWindow: 16384,
        maxTokens: 4096,
        supports: {
          chat: true,
          completion: true,
          functionCalling: true,
          vision: false,
          streaming: true,
          jsonMode: true,
        },
        pricing: {
          currency: 'USD',
          input: 0.0005,
          output: 0.0015,
        },
      },
    ],
  },
  {
    id: 'anthropic',
    name: 'Anthropic',
    icon: '🧠',
    description: 'Claude 3 family of models',
    website: 'https://anthropic.com',
    requiresApiKey: true,
    customizable: false,
    status: 'disconnected',
    models: [
      {
        id: 'claude-3-opus-20240229',
        name: 'Claude 3 Opus',
        description: 'Most powerful model for complex tasks',
        contextWindow: 200000,
        maxTokens: 4096,
        supports: {
          chat: true,
          completion: true,
          functionCalling: true,
          vision: true,
          streaming: true,
          jsonMode: false,
        },
        pricing: {
          currency: 'USD',
          input: 0.015,
          output: 0.075,
        },
      },
      {
        id: 'claude-3-5-sonnet-20241022',
        name: 'Claude 3.5 Sonnet',
        description: 'Latest Sonnet - balanced performance and cost',
        contextWindow: 200000,
        maxTokens: 8192,
        supports: {
          chat: true,
          completion: true,
          functionCalling: true,
          vision: true,
          streaming: true,
          jsonMode: false,
        },
        pricing: {
          currency: 'USD',
          input: 0.003,
          output: 0.015,
        },
      },
      {
        id: 'claude-3-5-haiku-20241022',
        name: 'Claude 3.5 Haiku',
        description: 'Latest Haiku - fast and affordable',
        contextWindow: 200000,
        maxTokens: 8192,
        supports: {
          chat: true,
          completion: true,
          functionCalling: true,
          vision: true,
          streaming: true,
          jsonMode: false,
        },
        pricing: {
          currency: 'USD',
          input: 0.001,
          output: 0.005,
        },
      },
    ],
  },
  {
    id: 'xai',
    name: 'xAI',
    icon: '🚀',
    description: 'Grok models from xAI',
    website: 'https://x.ai',
    requiresApiKey: true,
    customizable: false,
    status: 'disconnected',
    models: [
      {
        id: 'grok-beta',
        name: 'Grok Beta',
        description: 'Advanced reasoning with real-time knowledge',
        contextWindow: 8192,
        maxTokens: 4096,
        supports: {
          chat: true,
          completion: true,
          functionCalling: false,
          vision: false,
          streaming: true,
          jsonMode: false,
        },
      },
    ],
  },
];

export function AISettingsProvider({ 
  children,
  aiService,
}: { 
  children: ReactNode;
  aiService?: AIService;
}) {
  const [settings, setSettings] = useState<AISettings | null>(null);
  const [providers, setProviders] = useState<ProviderInfo[]>(PROVIDER_INFO);
  const [isLoading, setIsLoading] = useState(true);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  
  const settingsStore = typeof window !== 'undefined' ? new LocalStorageAISettingsStore() : null;
  const keyManager = typeof window !== 'undefined' ? new SecureAPIKeyManager() : null;

  // Load settings on mount
  useEffect(() => {
    if (typeof window !== 'undefined') {
      loadSettings();
    }
  }, [aiService]);

  const loadSettings = async () => {
    if (!settingsStore) return;
    
    try {
      setIsLoading(true);
      const loadedSettings = await settingsStore.load();
      setSettings(loadedSettings);
      
      // Update provider connection status
      await updateProviderStatuses(loadedSettings);
      
      // Configure AI service with loaded settings
      if (aiService && keyManager) {
        const apiKeys: Record<string, any> = {};
        
        // Retrieve actual API keys from secure storage
        if (await keyManager.hasKey('openai')) {
          const key = await keyManager.retrieve('openai');
          if (key) apiKeys.openai = { apiKey: key };
        }
        if (await keyManager.hasKey('anthropic')) {
          const key = await keyManager.retrieve('anthropic');
          if (key) apiKeys.anthropic = { apiKey: key };
        }
        if (await keyManager.hasKey('xai')) {
          const key = await keyManager.retrieve('xai');
          if (key) apiKeys.xai = { apiKey: key };
        }
        
        if (Object.keys(apiKeys).length > 0) {
          aiService.configure({
            providers: apiKeys,
            defaultProvider: loadedSettings.provider,
            defaultModel: loadedSettings.model,
          });
        }
      }
    } catch (error) {
      console.error('Failed to load AI settings:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const updateProviderStatuses = async (currentSettings: AISettings) => {
    const updatedProviders = await Promise.all(
      providers.map(async (provider) => {
        const hasKey = keyManager ? await keyManager.hasKey(provider.id) : false;
        return {
          ...provider,
          status: (hasKey ? 'connected' : 'disconnected') as 'connected' | 'disconnected' | 'error' | 'checking',
        };
      })
    );
    setProviders(updatedProviders);
  };

  const updateSettings = useCallback((updates: Partial<AISettings>) => {
    if (!settings) return;
    
    setSettings(prev => prev ? { ...prev, ...updates } : null);
    setHasUnsavedChanges(true);
  }, [settings]);

  const saveSettings = async () => {
    if (!settings || !settingsStore) return;
    
    try {
      await settingsStore.save(settings);
      setHasUnsavedChanges(false);
      await updateProviderStatuses(settings);
      
      // Configure AI service with the saved settings
      if (aiService && keyManager) {
        const apiKeys: Record<string, any> = {};
        
        // Retrieve actual API keys from secure storage
        if (await keyManager.hasKey('openai')) {
          const key = await keyManager.retrieve('openai');
          if (key) apiKeys.openai = { apiKey: key };
        }
        if (await keyManager.hasKey('anthropic')) {
          const key = await keyManager.retrieve('anthropic');
          if (key) apiKeys.anthropic = { apiKey: key };
        }
        if (await keyManager.hasKey('xai')) {
          const key = await keyManager.retrieve('xai');
          if (key) apiKeys.xai = { apiKey: key };
        }
        
        aiService.configure({
          providers: apiKeys,
          defaultProvider: settings.provider,
          defaultModel: settings.model,
        });
      }
    } catch (error) {
      console.error('Failed to save settings:', error);
      throw error;
    }
  };

  const resetSettings = async () => {
    if (!settingsStore) return;
    
    try {
      await settingsStore.reset();
      await loadSettings();
      setHasUnsavedChanges(false);
    } catch (error) {
      console.error('Failed to reset settings:', error);
      throw error;
    }
  };

  const exportSettings = async () => {
    if (!settingsStore) throw new Error('Settings store not available');
    return settingsStore.export();
  };

  const importSettings = async (data: string) => {
    if (!settingsStore) throw new Error('Settings store not available');
    
    try {
      await settingsStore.import(data);
      await loadSettings();
      setHasUnsavedChanges(false);
    } catch (error) {
      console.error('Failed to import settings:', error);
      throw error;
    }
  };

  const updateApiKey = async (provider: string, key: string) => {
    if (!settings || !keyManager) return;
    
    try {
      await keyManager.store(provider, key);
      setSettings(prev => prev ? {
        ...prev,
        apiKeys: { ...prev.apiKeys, [provider]: key }
      } : null);
      setHasUnsavedChanges(true);
      
      // Update provider status
      setProviders(prev => prev.map(p => 
        p.id === provider ? { ...p, status: 'connected' } : p
      ));
    } catch (error) {
      console.error('Failed to update API key:', error);
      throw error;
    }
  };

  const removeApiKey = async (provider: string) => {
    if (!settings || !keyManager) return;
    
    try {
      await keyManager.remove(provider);
      const { [provider]: _, ...remainingKeys } = settings.apiKeys;
      setSettings(prev => prev ? {
        ...prev,
        apiKeys: remainingKeys
      } : null);
      setHasUnsavedChanges(true);
      
      // Update provider status
      setProviders(prev => prev.map(p => 
        p.id === provider ? { ...p, status: 'disconnected' } : p
      ));
    } catch (error) {
      console.error('Failed to remove API key:', error);
      throw error;
    }
  };

  const validateApiKey = async (provider: string, key: string) => {
    if (!keyManager) return false;
    return keyManager.validate(provider, key);
  };

  const testConnection = async (provider: string): Promise<ConnectionTestResult> => {
    try {
      // Update status to checking
      setProviders(prev => prev.map(p => 
        p.id === provider ? { ...p, status: 'checking' } : p
      ));

      const startTime = Date.now();
      const isValid = await validateApiKey(provider, settings?.apiKeys[provider] || '');
      const latency = Date.now() - startTime;

      const result: ConnectionTestResult = {
        success: isValid,
        latency,
        error: isValid ? undefined : 'Invalid API key',
      };

      // Update provider status
      setProviders(prev => prev.map(p => 
        p.id === provider ? { 
          ...p, 
          status: isValid ? 'connected' : 'error',
          error: result.error,
        } : p
      ));

      return result;
    } catch (error) {
      const result: ConnectionTestResult = {
        success: false,
        error: error instanceof Error ? error.message : 'Connection test failed',
      };

      setProviders(prev => prev.map(p => 
        p.id === provider ? { ...p, status: 'error', error: result.error } : p
      ));

      return result;
    }
  };

  const switchProvider = (providerId: string) => {
    if (!settings) return;
    
    const provider = providers.find(p => p.id === providerId);
    if (!provider) return;

    // Also update model to first available for the provider
    const firstModel = provider.models[0];
    
    updateSettings({
      provider: providerId,
      model: firstModel?.id || '',
    });

    // Update AI service if available
    if (aiService) {
      aiService.switchProvider(providerId);
      if (firstModel) {
        aiService.switchModel(firstModel.id);
      }
    }
  };

  const switchModel = (modelId: string) => {
    if (!settings) return;
    
    updateSettings({ model: modelId });

    // Update AI service if available
    if (aiService) {
      aiService.switchModel(modelId);
    }
  };

  const estimateCost = (inputTokens: number, outputTokens: number): CostEstimate | null => {
    if (!settings) return null;

    const provider = providers.find(p => p.id === settings.provider);
    const model = provider?.models.find(m => m.id === settings.model);
    
    if (!model?.pricing) return null;

    const inputCost = (inputTokens / 1000) * model.pricing.input;
    const outputCost = (outputTokens / 1000) * model.pricing.output;

    return {
      provider: settings.provider,
      model: settings.model,
      inputTokens,
      outputTokens,
      inputCost,
      outputCost,
      totalCost: inputCost + outputCost,
      currency: model.pricing.currency,
    };
  };

  if (!settings) {
    return null;
  }

  return (
    <AISettingsContext.Provider
      value={{
        settings,
        providers,
        isLoading,
        hasUnsavedChanges,
        updateSettings,
        saveSettings,
        resetSettings,
        exportSettings,
        importSettings,
        updateApiKey,
        removeApiKey,
        validateApiKey,
        testConnection,
        switchProvider,
        switchModel,
        estimateCost,
      }}
    >
      {children}
    </AISettingsContext.Provider>
  );
}

export function useAISettings() {
  const context = useContext(AISettingsContext);
  if (context === undefined) {
    throw new Error('useAISettings must be used within an AISettingsProvider');
  }
  return context;
}